import type { AgeGroup, Staff } from '@baseball-near-you/prisma';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { clients } from '../clients';
import { QueryKey } from '../hooks/QueryKey';

export function useTeamAgeGroupMutation(args?: {
  teamId?: number;
  hideToast?: boolean;
}) {
  const { teamId, hideToast = false } = args ?? {};

  const queryClient = useQueryClient();
  const {
    mutateAsync: addAgeGroup,
    isLoading: isAddingAgeGroup,
    error: addAgeGroupError,
  } = useMutation({
    mutationFn: async (
      data: Omit<AgeGroup, 'id' | 'addressId' | 'teamId'>[]
    ) => {
      if (!teamId) {
        throw new Error('Team ID is required to add an age group');
      }
      const newAgeGroup = await clients.team.addAgeGroup({
        args: { teamId: teamId },
        payload: {
          newAgeGroups: data,
          teamId: teamId,
        },
      });
      return newAgeGroup;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries([QueryKey.Team, teamId]);
      if (!hideToast) {
        toast.success('Team updated!', {
          description: 'Your age group has been added successfully',
        });
      }
    },
  });

  const {
    mutateAsync: updateAgeGroup,
    isLoading: isUpdatingAgeGroup,
    error: updateAgeGroupError,
  } = useMutation({
    mutationFn: async (
      data: Partial<AgeGroup> & { id: number; staff?: Pick<Staff, 'id'>[] }
    ) => {
      if (!teamId) {
        throw new Error('Team ID is required to update an age group');
      }
      const updatedAgeGroup = await clients.team.updateAgeGroup({
        args: { ageGroupId: data.id, teamId: teamId },
        payload: data,
      });
      return updatedAgeGroup;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries([QueryKey.Team, teamId]);
      toast.success('Team updated!', {
        description: 'Your age group has been updated successfully',
      });
    },
  });

  return {
    addAgeGroup,
    isAddingAgeGroup,
    addAgeGroupError,
    updateAgeGroup,
    isUpdatingAgeGroup,
    updateAgeGroupError,
  };
}
